import { StripeCheckoutValue } from '@stripe/react-stripe-js/checkout';
import { PaymentRequest } from '@stripe/stripe-js';
import { Analytics } from '@util/Analytics';
import { Price } from '@util/Currency';
import { Environment } from '@util/Environment';
import { runActionSideEffect } from '@util/StoreUtils';
import { UserFriendlyError } from '@util/UserFriendlyError';
import { App, CheckoutResult, PriceValue, SubscriptionPlan, TrialPlan } from 'hydra-data-types';
import { Merge, pick } from 'ts-utils';
import { chooseAddonPlanPerExperiment } from '../../businessLogic/AddonPlans';
import { BASELINE_PURCHASE_PLAN } from '../../businessLogic/PlansData';
import { ActionType } from '../ActionTypes';
import { LOADED_STATE, LOADING_STATE } from '../LoadingAndErrorState';
import { StoreAction, store } from '../Store';
import { PricingState } from '../pricing/PricingState';
import { fetchPrices } from '../pricing/effects/FetchPrices';
import {
  CompletedPurchaseState,
  INITIAL_PURCHASE_STATE,
  InitialPurchaseState,
  PurchaseState,
  ReadyPurchaseState,
  ReadyWithExpressPayPurchaseState,
} from './PurchaseState';
import { createCheckout } from './effects/CreateCheckout';
import { purchase } from './effects/Purchase';

export type PurchaseAction =
  | Merge<
      {
        type: ActionType.SET_PAYMENT_PLAN;
      },
      Pick<PurchaseState, 'plan' | 'trialPlan' | 'trialPeriodDays'> & {
        addonProduct?: App;
      }
    >
  | Merge<
      {
        type: ActionType.SET_SELECTED_TRIAL_PLAN;
      },
      Pick<PurchaseState, 'trialPlan'>
    >
  | {
      type: ActionType.CREATE_CHECKOUT;
    }
  | {
      type: ActionType.SETUP_EXPRESS_PAY;
    }
  | {
      type: ActionType.CONFIRM_CHECKOUT;
      checkout: StripeCheckoutValue;
    }
  | {
      type: ActionType.SET_ADDON;
      addonPlan?: string;
    }
  | {
      type: ActionType.PAYMENT_SUCCESS;
      result: Pick<PricingState, 'currency' | 'couponCode'> & {
        isRecurring: boolean;
        paymentMethod: string;
        initialPlanPrice: PriceValue;
        checkoutId: string;
      };
    }
  | {
      type: ActionType.PAYMENT_FAILURE;
      error: UserFriendlyError;
      paymentMethod: string;
    };

export type PrivatePurchaseAction =
  | {
      type: ActionType.CREATE_CHECKOUT_LOADING;
    }
  | {
      type: ActionType.CREATE_CHECKOUT_SUCCESS;
      result: CheckoutResult;
    }
  | {
      type: ActionType.CREATE_CHECKOUT_FAILURE;
      error: UserFriendlyError;
    }
  | {
      type: ActionType.SETUP_EXPRESS_PAY_LOADING;
    }
  | {
      type: ActionType.SETUP_EXPRESS_PAY_SUCCESS;
      result: { paymentRequest: PaymentRequest };
    }
  | {
      type: ActionType.SETUP_EXPRESS_PAY_FAILURE;
      error: UserFriendlyError;
    }
  | {
      type: ActionType.PAYMENT_LOADING;
    }
  | {
      type: ActionType.SET_ADDON_PURCHASED;
    };

export function purchaseReducer(state: PurchaseState, action: StoreAction): PurchaseState {
  const purchasedProduct = state.addonProduct ?? Environment.APP;
  switch (action.type) {
    case ActionType.SET_PAYMENT_PLAN: {
      runActionSideEffect(createCheckout);
      runActionSideEffect(fetchPrices);
      return {
        ...state,
        plan: action.plan,
        trialPlan: action.trialPlan,
        trialPeriodDays: action.trialPeriodDays,
        addonProduct: action.addonProduct,
      };
    }
    case ActionType.SET_SELECTED_TRIAL_PLAN: {
      runActionSideEffect(createCheckout);
      const trialPeriodDays = action.trialPlan === TrialPlan.None ? 0 : state.trialPeriodDays;
      return { ...state, trialPlan: action.trialPlan, trialPeriodDays };
    }
    case ActionType.SET_ADDON: {
      runActionSideEffect(createCheckout);
      return { ...state, addonPlan: action.addonPlan };
    }
    case ActionType.CREATE_CHECKOUT: {
      runActionSideEffect(createCheckout);
      return state;
    }
    case ActionType.CONFIRM_CHECKOUT: {
      runActionSideEffect(purchase, action.checkout);
      return state;
    }
    case ActionType.CREATE_CHECKOUT_LOADING: {
      return {
        ...state,
        intent: { ...INITIAL_PURCHASE_STATE.intent, ...LOADING_STATE },
      } as InitialPurchaseState;
    }
    case ActionType.CREATE_CHECKOUT_SUCCESS: {
      return {
        ...state,
        intent: {
          ...pick(action.result, ['checkoutId', 'clientSecret', 'stripeAccount']),
          ...LOADED_STATE,
        },
      } as ReadyPurchaseState;
    }
    case ActionType.CREATE_CHECKOUT_FAILURE: {
      return {
        ...state,
        intent: {
          ...INITIAL_PURCHASE_STATE.intent,
          loading: false,
          error: action.error,
        },
      } as InitialPurchaseState;
    }
    case ActionType.SETUP_EXPRESS_PAY_LOADING: {
      return {
        ...state,
        expressPay: { ...INITIAL_PURCHASE_STATE.expressPay, ...LOADING_STATE },
      } as ReadyPurchaseState;
    }
    case ActionType.SETUP_EXPRESS_PAY_SUCCESS: {
      return {
        ...state,
        expressPay: {
          paymentRequest: action.result.paymentRequest,
          ...LOADED_STATE,
        },
      } as ReadyWithExpressPayPurchaseState;
    }
    case ActionType.SETUP_EXPRESS_PAY_FAILURE: {
      return {
        ...state,
        expressPay: {
          paymentRequest: undefined,
          loading: false,
          error: action.error,
        },
      } as ReadyPurchaseState;
    }
    case ActionType.PAYMENT_LOADING: {
      Analytics.track({ event: Analytics.Event.s_trial_start_requested });

      return { ...state, payment: LOADING_STATE } as ReadyPurchaseState;
    }
    case ActionType.PAYMENT_SUCCESS: {
      const purchaseProperties = pick(state, ['plan', 'trialPlan', 'trialPeriodDays']);

      Analytics.track({
        event: Analytics.Event.s_trial_started,
        ...purchaseProperties,
        purchaseType: action.result.isRecurring ? 'subscription' : 'one-off',
        purchasedProduct,
        addonPlan: state.addonPlan,
        price: Price.inMainUnit(action.result.initialPlanPrice),
        currency: action.result.currency,
        coupon: action.result.couponCode,
        paymentMethod: action.result.paymentMethod,
        from: 'website',
      });

      return {
        ...state,
        checkoutId: action.result.checkoutId,
        payment: LOADED_STATE,
        purchasedPlanPrice: new Price(
          action.result.initialPlanPrice,
          action.result.currency
        ).toString(),
      } as CompletedPurchaseState;
    }
    case ActionType.PAYMENT_FAILURE: {
      Analytics.track({
        event: Analytics.Event.s_trial_start_failed,
        error: action.error.message,
        paymentMethod: action.paymentMethod,
        purchasedProduct,
      });

      return {
        ...state,
        payment: { loading: false, error: action.error },
      } as ReadyPurchaseState;
    }
    case ActionType.SET_ADDON_PURCHASED: {
      return { ...state, addonPurchased: true };
    }
    default:
      return state;
  }
}

export const PurchaseSelector = {
  hasTrial(state: PurchaseState): boolean {
    return state.trialPlan !== TrialPlan.None;
  },

  hasFreeTrial(state: PurchaseState): boolean {
    return state.trialPlan === TrialPlan.FreeTrial;
  },

  baselinePlan(): SubscriptionPlan {
    return BASELINE_PURCHASE_PLAN;
  },

  addonPlan(state: PurchaseState): string | undefined {
    return state.addonPlan;
  },
};

/**
 * Set addon plan based on experiment variant for Nerva
 */
export function dispatchSetAddonProduct(hasAddon: boolean): void {
  store.dispatch({
    type: ActionType.SET_ADDON,
    addonPlan: hasAddon ? chooseAddonPlanPerExperiment() : undefined,
  });
}
